import React, {useCallback, useState} from "react";
import {Row, Descriptions, DatePicker, Select, InputNumber, Input, Form} from "antd";
import {CustomButton} from "../../../_defaultComponents/CustomButton/CustomButton";
import moment from "moment";
import {EditOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../../hooks/useStore";
import {useFormik} from "formik";
import * as yup from "yup";
import {useFetch} from "../../../../hooks/useFetch";
import {UserSelectInput} from "../../../_defaultComponents/UserSelectInput/UserSelectInput";
import {useOptions} from "../../../../hooks/useOptions";

const MODES = {
    VIEW: "view",
    EDIT: "edit"
};
const DEFAULT_MODE = MODES.VIEW;
export const SubCompanySalesTab = ({subcompanyId, subCompany}) => {
    const stores = useStore();
    const {t, i18n} = useTranslation(["subcompanyDetail", "courierTypes"]);
    const [mode, setMode] = useState(DEFAULT_MODE);
    const {optionLoading, options, findInOptions} = useOptions({groupName: "subCompanyDetail"});
    const handleOnChangeMode = () => {
        setMode(mode === MODES.VIEW ? MODES.EDIT : MODES.VIEW);
    };
    const [subcompanyDetail, setSubcompanyDetail] = useState({});
    const [recall, setRecall] = useState(false);

    const [_, subCompanyDetailData] = useFetch({
        fetchFn: stores.companyStore.getSubcompanyDetail.bind(null, {
            subcompanyId: subcompanyId
        }),

        defaultDataValue: {},
        showLoading: false,
        initialCall: true,
        condition: Boolean(subcompanyId),
        dependencies: [subcompanyId, recall],
        callbackFunc: data => {
            setSubcompanyDetail(data);
        }
    });

    const [, regionalDirectorie = {}] = useFetch({
        fetchFn: stores.userStore.getUser.bind(null, {
            userId: subCompanyDetailData?.regionalDirectorie
        }),
        showLoading: true,
        condition: Boolean(subCompanyDetailData?.regionalDirectorie),
        dependencies: [subCompanyDetailData?.regionalDirectorie],
        defaultDataValue: {}
    });

    //#region formik
    const requiredMessage = () => i18n.t("validation.required", {ns: "common"});
    const emailFormatMessage = () => i18n.t("validation.emailFormat", {ns: "common"});

    const formik = useFormik({
        initialValues: {
            openedDate: subcompanyDetail?.openedDate
                ? moment(subcompanyDetail.openedDate)
                : undefined,
            // workingPeriod: subCompany?.workingPeriod,
            statusCode: subcompanyDetail?.statusCode,
            sector: subcompanyDetail?.sector,
            priority: Boolean(subcompanyDetail?.priority),
            category: subcompanyDetail?.category,
            regionalDirectoriePlace: subcompanyDetail?.regionalDirectoriePlace,
            place: subcompanyDetail?.place,
            regionalDirectorie: subcompanyDetail?.regionalDirectorie,
            tp: subcompanyDetail?.tp,
            courierCount: subcompanyDetail?.courierCount,
            billingPeriod: subcompanyDetail?.billingPeriod,
            price: subcompanyDetail?.price,
            maturityDay: subcompanyDetail?.maturityDay,
            //priceAgreement: subcompanyDetail?.priceAgreement,
            mail: subcompanyDetail?.mail
        },
        validationSchema: yup.object().shape({
            openedDate: yup.string().required(requiredMessage),
            statusCode: yup.string().required(requiredMessage),
            sector: yup.string().required(requiredMessage),
            priority: yup.boolean().required(requiredMessage),
            category: yup.string().required(requiredMessage),
            regionalDirectoriePlace: yup.string().required(requiredMessage),
            place: yup.string().required(requiredMessage),
            regionalDirectorie: yup.string().required(requiredMessage),
            tp: yup.number().required(requiredMessage),
            courierCount: yup.number().required(requiredMessage),
            billingPeriod: yup.string().required(requiredMessage),
            price: yup.number().required(requiredMessage),
            maturityDay: yup.string().required(requiredMessage),
            mail: yup
                .string()
                .email(emailFormatMessage)
                .required(requiredMessage)
        }),
        enableReinitialize: true,
        onSubmit: async values => {
            try {
                const payload = {
                    openedDate: values?.openedDate
                        ? moment(values.openedDate).format("YYYY-MM-DD")
                        : undefined,
                    // workingPeriod: values?.workingPeriod,
                    statusCode: values?.statusCode,
                    sector: values?.sector,
                    priority: Boolean(values?.priority),
                    category: values?.category,
                    regionalDirectoriePlace: values?.regionalDirectoriePlace,
                    place: values?.place,
                    regionalDirectorie: values?.regionalDirectorie,
                    tp: values?.tp,
                    courierCount: values?.courierCount,
                    billingPeriod: values?.billingPeriod,
                    price: values?.price,
                    maturityDay: values?.maturityDay,
                    //priceAgreement: values?.priceAgreement,
                    mail: values?.mail
                };

                const result = await stores.companyStore.updateSubcompanyDetail({
                    ...payload,
                    subCompanyId: subcompanyId
                });

                setRecall(!recall);
                setMode(MODES.VIEW);
            } catch (error) {
                console.log(error);
                //Sentry.captureException(error);
            }
        }
    });

    const {values, errors, touched, isSubmitting, handleSubmit, setFieldValue} = formik;
    //#endregion
    const renderOn = useCallback(
        ({_mode = mode, view, edit}) => {
            if (!subcompanyDetail) return <></>;
            switch (_mode) {
                case MODES.VIEW:
                    return view;
                case MODES.EDIT:
                    return edit;
                default:
                    return view;
            }
        },
        [mode, subcompanyDetail]
    );

    return (
        <>
            <Form
                autoComplete="off"
                labelCol={{span: 24}}
                wrapperCol={{span: 24}}
                onFinish={handleSubmit}
                data-testid="addSubCompanyForm"
            >
                <Descriptions
                    column={{xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1}}
                    title={
                        <>
                            <Row type="flex" justify="space-between">
                                Satış Bilgileri
                                {stores.meStore.checkRole("updateCompany") && (
                                    <CustomButton
                                        type="primary"
                                        disabled={subCompany.data.disabled}
                                        icon={<EditOutlined />}
                                        onClick={handleOnChangeMode}
                                    />
                                )}
                            </Row>
                        </>
                    }
                    bordered
                >
                    <Descriptions.Item span={1} label="Açılış Tarihi">
                        {renderOn({
                            view: values?.openedDate
                                ? moment(values.openedDate).format("DD.MM.YYYY")
                                : "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.openedDate && touched.openedDate
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.openedDate && touched.openedDate
                                            ? errors.openedDate
                                            : null
                                    }
                                >
                                    <DatePicker
                                        style={{width: "100%"}}
                                        format="DD.MM.YYYY"
                                        value={values?.openedDate}
                                        onChange={date => setFieldValue("openedDate", date)}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Çalışma Süresi">
                        {subcompanyDetail?.workingMonths
                            ? `${subcompanyDetail?.workingMonths} Ay`
                            : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Durum Kodu">
                        {renderOn({
                            view: findInOptions({
                                group: "statusCode",
                                value: values?.statusCode
                            }),
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.statusCode && touched.statusCode
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.statusCode && touched.statusCode
                                            ? errors.statusCode
                                            : null
                                    }
                                >
                                    <Select
                                        loading={optionLoading}
                                        style={{width: "100%"}}
                                        value={values?.statusCode}
                                        onChange={e => setFieldValue("statusCode", e)}
                                    >
                                        {Array.isArray(options?.statusCode) &&
                                            options.statusCode.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {findInOptions({
                                                        group: "statusCode",
                                                        value: item?.value
                                                    })}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Sektör">
                        {renderOn({
                            view:
                                findInOptions({
                                    group: "sector",
                                    value: subcompanyDetail?.sector
                                }) || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.sector && touched.sector ? "error" : "success"
                                    }
                                    help={errors.sector && touched.sector ? errors.sector : null}
                                >
                                    <Select
                                        loading={optionLoading}
                                        style={{width: "100%"}}
                                        value={values?.sector}
                                        onChange={e => setFieldValue("sector", e)}
                                    >
                                        {Array.isArray(options?.sector) &&
                                            options.sector.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {findInOptions({
                                                        group: "sector",
                                                        value: item?.value
                                                    })}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Öncelik">
                        {renderOn({
                            view: values?.priority ? "Evet" : "Hayır",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.priority && touched.priority ? "error" : "success"
                                    }
                                    help={
                                        errors.priority && touched.priority ? errors.priority : null
                                    }
                                >
                                    <Select
                                        style={{width: "100%"}}
                                        value={values?.priority}
                                        onChange={e => setFieldValue("priority", e)}
                                    >
                                        <Select.Option value={true}>Evet</Select.Option>
                                        <Select.Option value={false}>Hayır</Select.Option>
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Kategori">
                        {renderOn({
                            view: findInOptions({
                                group: "category",
                                value: values?.category
                            }),
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.category && touched.category ? "error" : "success"
                                    }
                                    help={
                                        errors.category && touched.category ? errors.category : null
                                    }
                                >
                                    <Select
                                        loading={optionLoading}
                                        style={{width: "100%"}}
                                        value={values?.category}
                                        onChange={e => setFieldValue("category", e)}
                                    >
                                        {Array.isArray(options?.category) &&
                                            options.category.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {findInOptions({
                                                        group: "category",
                                                        value: item?.value
                                                    })}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Bölge Müdürlüğü">
                        {renderOn({
                            view: findInOptions({
                                group: "regionalDirectoriePlace",
                                keyName: "name",
                                value: values?.regionalDirectoriePlace
                            }),
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.regionalDirectoriePlace &&
                                        touched.regionalDirectoriePlace
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.regionalDirectoriePlace &&
                                        touched.regionalDirectoriePlace
                                            ? errors.regionalDirectoriePlace
                                            : null
                                    }
                                >
                                    <Select
                                        loading={optionLoading}
                                        style={{width: "100%"}}
                                        value={values?.regionalDirectoriePlace}
                                        onChange={e => setFieldValue("regionalDirectoriePlace", e)}
                                    >
                                        {Array.isArray(options?.statusCode) &&
                                            options.regionalDirectoriePlace.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {item?.name}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Fiyat Bölgesi">
                        {renderOn({
                            view: findInOptions({
                                group: "subCompanyDetailPlace",
                                keyName: "name",
                                value: findInOptions({
                                    group: "subCompanyDetailPlace",
                                    value: values?.place
                                })
                            }),
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.place && touched.place ? "error" : "success"
                                    }
                                    help={errors.place && touched.place ? errors.place : null}
                                >
                                    <Select
                                        loading={optionLoading}
                                        style={{width: "100%"}}
                                        value={values?.place}
                                        onChange={e => setFieldValue("place", e)}
                                    >
                                        {Array.isArray(options?.subCompanyDetailPlace) &&
                                            options.subCompanyDetailPlace.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {item?.name}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Bölge Müdürü">
                        {renderOn({
                            view: regionalDirectorie?.data?.fullName || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.regionalDirectorie && touched.regionalDirectorie
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.regionalDirectorie && touched.regionalDirectorie
                                            ? errors.regionalDirectorie
                                            : null
                                    }
                                >
                                    <UserSelectInput
                                        showTitle={false}
                                        role="sOperation"
                                        initialCall={true}
                                        value={values?.regionalDirectorie}
                                        onChange={e => setFieldValue("regionalDirectorie", e)}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="TP">
                        {renderOn({
                            view: values?.tp || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={errors.tp && touched.tp ? "error" : "success"}
                                    help={errors.tp && touched.tp ? errors.tp : null}
                                >
                                    <InputNumber
                                        style={{width: "100%"}}
                                        value={values?.tp}
                                        onChange={e => setFieldValue("tp", e)}
                                        min={0}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Kurye Sayısı">
                        {renderOn({
                            view: values?.courierCount || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.courierCount && touched.courierCount
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.courierCount && touched.courierCount
                                            ? errors.courierCount
                                            : null
                                    }
                                >
                                    <InputNumber
                                        style={{width: "100%"}}
                                        value={values?.courierCount}
                                        onChange={e => setFieldValue("courierCount", e)}
                                        min={0}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Fatura Dönemi">
                        {renderOn({
                            view: findInOptions({
                                group: "billingPeriod",
                                value: values?.billingPeriod
                            }),
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.billingPeriod && touched.billingPeriod
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.billingPeriod && touched.billingPeriod
                                            ? errors.billingPeriod
                                            : null
                                    }
                                >
                                    <Select
                                        loading={optionLoading}
                                        style={{width: "100%"}}
                                        value={values?.billingPeriod}
                                        onChange={e => setFieldValue("billingPeriod", e)}
                                    >
                                        {Array.isArray(options?.billingPeriod) &&
                                            options.billingPeriod.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {findInOptions({
                                                        group: "billingPeriod",
                                                        value: item?.value
                                                    })}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Ödeme Tutarı (KDV Hariç)">
                        {renderOn({
                            view: values?.price || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.price && touched.price ? "error" : "success"
                                    }
                                    help={errors.price && touched.price ? errors.price : null}
                                >
                                    <InputNumber
                                        style={{width: "100%"}}
                                        value={values?.price}
                                        onChange={e => setFieldValue("price", e)}
                                        min={0}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Vade Günü">
                        {renderOn({
                            view: findInOptions({
                                group: "maturityDay",
                                value: values?.maturityDay
                            }),
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.maturityDay && touched.maturityDay
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.maturityDay && touched.maturityDay
                                            ? errors.maturityDay
                                            : null
                                    }
                                >
                                    <Select
                                        style={{width: "100%"}}
                                        value={values?.maturityDay}
                                        onChange={e => setFieldValue("maturityDay", e)}
                                    >
                                        {Array.isArray(options?.maturityDay) &&
                                            options.maturityDay.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {findInOptions({
                                                        group: "maturityDay",
                                                        value: item?.value
                                                    })}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Fiyat Anlaşması">
                        {renderOn({
                            view:
                                findInOptions({
                                    group: "priceAgreement",
                                    value: values?.priceAgreement
                                }) || "-"
                            /* edit: (
                            <Select
                                loading={optionLoading}
                                style={{width: "100%"}}
                                value={values?.priceAgreement}
                                onChange={e => setFieldValue("priceAgreement", e)}
                            >
                                {Array.isArray(options?.priceAgreement) &&
                                    options.priceAgreement.map(item => (
                                        <Select.Option key={item?.key} value={item?.value}>
                                            {item?.key}
                                        </Select.Option>
                                    ))}
                            </Select>
                        ) */
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Satışı Yapan">
                        {subcompanyDetail?.sellerBy || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Puantaj Yetkilisi Mail">
                        {renderOn({
                            view: values?.mail || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.mail && touched.mail ? "error" : "success"
                                    }
                                    help={errors.mail && touched.mail ? errors.mail : null}
                                >
                                    <Input
                                        type="email"
                                        style={{width: "100%"}}
                                        value={values?.mail}
                                        onChange={e => setFieldValue("mail", e.target.value)}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                </Descriptions>
                {mode === MODES.EDIT && (
                    <Row type="flex" justify={"end"}>
                        <CustomButton type="primary" htmlType="submit" disabled={isSubmitting}>
                            {t("actions.update", {ns: "common"})}
                        </CustomButton>
                    </Row>
                )}
            </Form>
            <Descriptions
                column={{xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1}}
                title={
                    <>
                        <Row type="flex" justify="space-between">
                            Kayıp Bilgileri
                        </Row>
                    </>
                }
                style={{marginTop: "30px"}}
                bordered
            >
                <Descriptions.Item span={1} label="Kayıp Ayı">
                    {subcompanyDetail?.lostMonth
                        ? moment(subcompanyDetail?.lostMonth, "M").format("MMMM")
                        : "-"}
                </Descriptions.Item>
                <Descriptions.Item span={1} label="Kayıp Tarihi">
                    {subcompanyDetail?.lostDate
                        ? moment(subcompanyDetail?.lostDate)?.format("DD.MM.YYYY")
                        : "-"}
                </Descriptions.Item>
                <Descriptions.Item span={1} label="Kayıp Nedeni">
                    {findInOptions({
                        group: "lostReason",
                        value: subcompanyDetail?.lostSubject
                    }) || "-"}
                </Descriptions.Item>
                <Descriptions.Item span={1} label="Kime Kaybedildi">
                    {subcompanyDetail?.lostedBy || "-"}
                </Descriptions.Item>
            </Descriptions>
        </>
    );
};
